import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {useCorregirExamen, type DecisionNorma, type ExamenOcupacional} from '@/hooks/sgsst/evidencias/use-examenes'
import {translateApiError} from '@/lib/errors'
import {formatDate, formatDateOnly} from '@/lib/risk'
import {cn} from '@/lib/utils'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Sin «convertir a apto con restricciones»: sería una restricción que ningún médico escribió
 * (E1-H06). La decisión viaja como dato para que un auditor sepa cuál fue. */
const ETIQUETA_ACCION: Record<DecisionNorma, string> = {
  solicitar_ips: 'solicitarIps',
  reasignar_cargo: 'reasignarCargo',
  marcar_norma_anterior: 'marcar',
  quitar_norma_anterior: 'quitar',
}

/** Un examen etiquetado «norma anterior» no va en rojo por viejo: su color sale del vencimiento. */
export function ExamenNormaAnteriorSellos({examen}: {examen: ExamenOcupacional}) {
  const {t} = useTranslation()
  const esNoApto = examen.concepto_aptitud === 'no_apto'
  if (!examen.norma_anterior && !esNoApto) return null
  return (
    <div className="mt-1 space-y-1" data-testid={`examen-norma-anterior-${examen.id}`}>
      <div className="flex flex-wrap gap-1.5">
        {examen.norma_anterior && <Tag tone="neutral">{t('examenes.normaAnterior.etiqueta')}</Tag>}
        {/* Sigue siendo transcripción de un certificado aunque reciba la etiqueta y deje de estar en rojo. */}
        {examen.veredicto_textual_declarado && (
          <Tag tone={examen.deshabilita ? 'red' : 'neutral'}>{t('examenes.normaAnterior.textualEtiqueta')}</Tag>
        )}
      </div>
      {/* La frase afirma el origen del registro: solo va cuando ese origen consta. */}
      {(examen.norma_anterior || examen.veredicto_textual_declarado) && (
        <p className="text-xs text-muted-foreground">{t('examenes.normaAnterior.frase')}</p>
      )}
      {examen.deshabilita && (
        <p className="text-xs font-medium text-foreground" data-testid={`examen-deshabilita-${examen.id}`}>
          {t('examenes.normaAnterior.noAptoFila')} {t('examenes.normaAnterior.deshabilitaHint')}
        </p>
      )}
      {examen.requiere_revision_norma && (
        <p className="text-xs text-muted-foreground" data-testid={`examen-pendiente-norma-${examen.id}`}>
          {t('examenes.normaAnterior.pendiente')}
        </p>
      )}
      {/* Una decisión escrita no apaga el seguimiento: la constancia no es un concepto nuevo. */}
      {examen.deshabilita && !examen.requiere_revision_norma && (
        <p className="text-xs text-muted-foreground" data-testid={`examen-decision-pendiente-${examen.id}`}>
          {t(
            examen.veredicto_textual_declarado
              ? 'examenes.normaAnterior.pendienteDeclarado'
              : 'examenes.normaAnterior.pendienteSinDeclarar'
          )}
        </p>
      )}
      {/* Cada decisión pisa la anterior: solo se muestra la última. */}
      {examen.norma_anterior_decision && examen.norma_anterior_corregida_en && (
        <p className="text-xs text-muted-foreground" data-testid={`examen-norma-anterior-revisado-${examen.id}`}>
          {t('examenes.normaAnterior.revisadoEl', {
            decision: t(`examenes.normaAnterior.decisiones.${examen.norma_anterior_decision}`),
            fecha: formatDate(examen.norma_anterior_corregida_en),
            nombre: examen.norma_anterior_corregida_por_nombre ?? t('examenes.normaAnterior.revisadoPorAlguien'),
            motivo: examen.norma_anterior_corregida_motivo ?? '',
          })}
        </p>
      )}
    </div>
  )
}

/** No manda datos médicos de vuelta: no puede pisar una corrección hecha con la pestaña abierta. */
function DecisionDialog({
  businessId,
  employeeId,
  examen,
  decisiones,
  onClose,
}: {
  businessId: string
  employeeId: string
  examen: ExamenOcupacional
  decisiones: DecisionNorma[]
  onClose: () => void
}) {
  const {t} = useTranslation()
  const [decision, setDecision] = useState<DecisionNorma | null>(null)
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false)
  const corregir = useCorregirExamen(businessId, employeeId)

  /** No autocompleta el motivo: es el único registro que lee un auditor y su puerta (422) exige un
   * acto humano. */
  function elegir(next: DecisionNorma) {
    setDecision(next)
    setMotivoError(undefined)
  }

  function pedirCierre() {
    if (motivo.trim()) return setConfirmandoDescarte(true)
    onClose()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(motivo.trim())
    setMotivoError(err ?? undefined)
    if (err || decision === null) return
    setError(null)
    try {
      await corregir.mutateAsync({
        examenId: examen.id,
        patch: {
          decision,
          norma_anterior_motivo: motivo.trim(),
          veredicto_textual_certificado: false,
          // Si otro custodio decidió mientras tanto, el backend responde 409 en vez de pisarlo.
          decision_vista_en: examen.norma_anterior_corregida_en,
        },
      })
      onClose()
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <>
      {/* Se repliega bajo la confirmación de descarte: dos modales a la vez se pelean el foco. */}
      <Dialog open={!confirmandoDescarte} onOpenChange={(next) => !next && pedirCierre()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('examenes.normaAnterior.acciones.decidir')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <p className="text-xs text-muted-foreground">{t('examenes.normaAnterior.dialogo.sinConversion')}</p>
            {/* Hay una sola ranura (bitácora en PQ-12): quien llega segundo ve lo que va a reemplazar. */}
            {examen.norma_anterior_decision && examen.norma_anterior_corregida_en && (
              <p className="text-xs font-medium text-foreground" data-testid={`examen-decision-previa-${examen.id}`}>
                {t('examenes.normaAnterior.dialogo.decisionPrevia', {
                  decision: t(`examenes.normaAnterior.decisiones.${examen.norma_anterior_decision}`),
                  fecha: formatDate(examen.norma_anterior_corregida_en),
                  nombre: examen.norma_anterior_corregida_por_nombre ?? t('examenes.normaAnterior.revisadoPorAlguien'),
                  motivo: examen.norma_anterior_corregida_motivo ?? '',
                })}
              </p>
            )}
            <div className="space-y-2">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('examenes.normaAnterior.dialogo.decisionLabel')}
              </span>
              <div className="flex flex-col gap-2">
                {decisiones.map((opcion) => (
                  <button
                    key={opcion}
                    type="button"
                    data-testid={`examen-norma-anterior-${ETIQUETA_ACCION[opcion]}-${examen.id}`}
                    onClick={() => elegir(opcion)}
                    className={cn(
                      'min-h-11 w-full rounded-lg border px-3 text-left text-sm font-medium',
                      decision === opcion ? 'border-primary bg-accent text-accent-foreground' : 'border-border'
                    )}>
                    {t(`examenes.normaAnterior.acciones.${ETIQUETA_ACCION[opcion]}`)}
                  </button>
                ))}
              </div>
            </div>
            {/* Kairos no pide nada a la IPS (PQ-6) ni reubica a nadie (PQ-11): solo guarda la constancia. */}
            {(decision === 'solicitar_ips' || decision === 'reasignar_cargo') && (
              <p className="text-xs text-muted-foreground" data-testid={`examen-sin-efecto-${examen.id}`}>
                {t(
                  decision === 'solicitar_ips'
                    ? 'examenes.normaAnterior.dialogo.sinSolicitud'
                    : 'examenes.normaAnterior.dialogo.sinReasignacion'
                )}
              </p>
            )}
            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('examenes.normaAnterior.dialogo.motivoLabel')}
              </span>
              <Textarea
                maxLength={tope('NOTA')}
                value={motivo}
                data-testid="examen-norma-anterior-motivo"
                onChange={(e) => {
                  setMotivo(e.target.value)
                  setMotivoError(undefined)
                }}
                onBlur={(e) => setMotivoError(validateRequired(e.target.value.trim()) ?? undefined)}
                aria-invalid={!!motivoError}
              />
            </label>
            <FieldError error={motivoError} />
            <p className="text-xs text-muted-foreground" data-testid={`examen-dato-fecha-${examen.id}`}>
              {t('examenes.normaAnterior.dialogo.datoExamen', {fecha: formatDateOnly(examen.fecha)})}
            </p>
            <p className="text-xs text-muted-foreground">{t('examenes.normaAnterior.dialogo.motivoHint')}</p>
            <FormDialogFooter
              error={error}
              pending={corregir.isPending}
              submitDisabled={decision === null}
              submitLabel={t('examenes.normaAnterior.dialogo.submit')}
              onCancel={pedirCierre}
            />
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmandoDescarte}
        onOpenChange={(open) => !open && setConfirmandoDescarte(false)}
        title={t('examenes.normaAnterior.dialogo.descartarTitulo')}
        description={t('examenes.normaAnterior.dialogo.descartarDescripcion')}
        confirmLabel={t('examenes.normaAnterior.dialogo.descartar')}
        cancelLabel={t('common.cancel')}
        onConfirm={onClose}
      />
    </>
  )
}

/** A quien no custodia no se le esconde la acción: se le apaga con su razón (spec §10). */
export function ExamenNormaAnteriorSalidas({
  businessId,
  employeeId,
  examen,
  canDecidir,
  bloqueadoPor,
}: {
  businessId: string
  employeeId: string
  examen: ExamenOcupacional
  canDecidir: boolean
  bloqueadoPor: string | null
}) {
  const {t} = useTranslation()
  const [abierta, setAbierta] = useState(false)
  if (examen.concepto_aptitud !== 'no_apto') return null
  const decisiones: DecisionNorma[] = examen.norma_anterior
    ? ['quitar_norma_anterior']
    : ['solicitar_ips', 'reasignar_cargo', 'marcar_norma_anterior']
  return (
    <div className="mt-1.5 space-y-1">
      <Button
        variant="outline"
        size="sm"
        disabled={!canDecidir}
        data-testid={`examen-norma-anterior-decidir-${examen.id}`}
        onClick={() => setAbierta(true)}>
        {t('examenes.normaAnterior.acciones.decidir')}
      </Button>
      {!canDecidir && bloqueadoPor && (
        <p className="text-xs text-muted-foreground" data-testid={`examen-norma-anterior-bloqueo-${examen.id}`}>
          {bloqueadoPor}
        </p>
      )}
      {abierta && (
        <DecisionDialog
          businessId={businessId}
          employeeId={employeeId}
          examen={examen}
          decisiones={decisiones}
          onClose={() => setAbierta(false)}
        />
      )}
    </div>
  )
}
